import * as React from 'react';
import { DataModel } from '@blazemeter/blaze-data-model';
import { ManipulatorDataModel } from '@blazemeter/blaze-data-designer-client';
import FullPageLoader from 'components/loaders/FullPageLoader';
import _ from 'lodash';
import { MSDataPreview } from '../MockServiceDataSettings';
import { downloadJsonPayloadAsCsv, getErrorMessage } from 'utils/miscUtil';
import axios from 'axios/axios-base';
import { getFileNameFromDownloadLink } from 'utils/blazeDataUtils';
import { useDispatch } from 'react-redux';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import MixpanelService from 'utils/mixpanelService';
import { DOWNLOAD_BLAZE_DATA } from 'utils/mixpanelEvents';
import BzmIcon from 'components/icons/BzmIcon';
import { TableEditor } from '@blazemeter/blaze-data-ui';
import BlurredLoader from 'components/overlays/BlurredLoader';
import DownloadBtn from '../Buttons/DownloadBtn';
import Placeholder from 'components/placeholder/Placeholder';

type MockServicePreviewDataSampleProps = {
  dataModel: DataModel;
  manipulatorDataModel: ManipulatorDataModel | null;
  activeEntity?: string;
  isLoading: boolean;
  dataPreview: MSDataPreview;
  isExportInProgress: boolean;
  isError: boolean;
  refreshBtn?: any;
};

const MockServicePreviewDataSample = (props: MockServicePreviewDataSampleProps) => {
  const dispatch = useDispatch();

  if (props.isLoading) {
    return <FullPageLoader loadingText={'Generating Preview...'} />;
  }

  if (_.isNil(props.activeEntity)) {
    return <p>No Entity Selected.</p>;
  }

  const isDataPreviewEmpty =
    _.isNil(props.dataPreview) || _.get(props.dataPreview, 'tableHeaders.columns.length') === 0;

  const downloadHandler = async () => {
    MixpanelService.track(DOWNLOAD_BLAZE_DATA());
    if (!props.dataPreview.downloadLink) {
      return;
    }

    try {
      const fileName = getFileNameFromDownloadLink(props.dataPreview.downloadLink);
      const downloadResponseFile = (
        await axios.get(props.dataPreview.downloadLink, {
          responseType: 'json',
        })
      ).data;

      // Get JSON representing entity
      const entityJson = downloadResponseFile[props.activeEntity];
      downloadJsonPayloadAsCsv(entityJson.data, fileName);
    } catch (error) {
      dispatch(showToastNotification(ToastType.Danger, 'Unable to download the file', getErrorMessage(error)));
    }
  };

  const getActionButtons = () => {
    return (
      <>
        {/*Download Button*/}
        {props.dataPreview.isFixed && <DownloadBtn onDownload={downloadHandler} />}

        {/* Refresh Button */}
        {props.refreshBtn}
      </>
    );
  };

  const getHeaderDom = () => {
    const count = `(${props.dataPreview.settings.title || 0})`;

    return (
      <div className={'mt-3 mb-3 clearfix'}>
        {props.dataPreview.isFixed ? (
          <h2 className={'mr-2 font-weight-semi-bold'}>
            Virtual Service Data <span className={'text-muted'}>{count}</span>
            {getActionButtons()}
          </h2>
        ) : (
          <div>
            <span className={'color-attention-yellow mr-2'}>
              <BzmIcon
                variant={'icon-bzm-eye'}
                className={'icon-xs mr-2'}
                style={{ position: 'relative', top: '2px' }}
              />
              Preview of Data Sample {count}
            </span>
            {getActionButtons()}
          </div>
        )}
      </div>
    );
  };

  if (props.isError === true) {
    return (
      <Placeholder
        title={'Cannot show Data Sample'}
        message={'An error happened during the load of the data sample.'}
        icon={'danger'}
      />
    );
  }

  return (
    <>
      {getHeaderDom()}

      <BlurredLoader isLoading={props.isExportInProgress}>
        {isDataPreviewEmpty ? (
          <p>No preview data available.</p>
        ) : (
          <div className="mt-3">
            <TableEditor
              csvData={props.dataPreview as any}
              allowHeadingEdit={false}
              hideLoadingSpinner={false}
              tableHeight={500}
            />
          </div>
        )}
      </BlurredLoader>
    </>
  );
};

export default MockServicePreviewDataSample;
